'use client';

import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import AppImage from '@/components/ui/AppImage';
import { useTranslation } from 'react-i18next';

export default function FinalCTA() {
  const { t } = useTranslation('home');
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  const handleBuyNow = () => {
    document.getElementById('product')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      id="final-cta"
      ref={ref}
      // FIX 1: Reduced mobile padding to `py-16` and added `overflow-x-clip` 
      // to ensure the background gradients don't cause horizontal scrollbars.
      className="py-16 lg:py-16 relative overflow-x-clip"
      aria-labelledby="cta-heading">
      
      {/* Accent background */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50 to-blue-pale" />
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 30% 50%, rgba(26,60,46,0.08) 0%, transparent 60%), radial-gradient(ellipse at 70% 50%, rgba(42,127,191,0.06) 0%, transparent 60%)'
        }}
        aria-hidden="true" />
        
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          
          {/* Left: Content */}
          <motion.div
            className="col-span-1 lg:col-span-6 order-2 lg:order-1"
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2, duration: 0.7 }}>
              
              <span className="text-xs font-semibold tracking-widest uppercase text-muted mb-4 lg:mb-6 block text-center lg:text-left">
                {t('finalCta.label')}
              </span>
            </motion.div>

            <motion.h2
              id="cta-heading"
              // FIX 2: Added explicit responsive typography sizing
              className="section-headline text-4xl sm:text-5xl lg:text-6xl font-display font-light text-foreground mb-6 lg:mb-8 text-center lg:text-left leading-tight"
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
              
              {t('finalCta.title.line1')}
              <br className="hidden sm:block" />
              {t('finalCta.title.line2')}{' '}
              <span className="italic pl-0.5 pr-2 text-gradient-green">{t('finalCta.title.highlight')}</span>
            </motion.h2>

            <motion.p
              className="text-muted text-base lg:text-lg leading-relaxed font-light mb-8 lg:mb-10 max-w-lg mx-auto lg:mx-0 text-center lg:text-left"
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4, duration: 0.8 }}>
              
              {t('finalCta.description')}
            </motion.p>

            {/* Price + CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="space-y-6 flex flex-col items-center lg:items-start">
              
              {/* <div className="flex items-center gap-4">
                <span className="font-display text-4xl lg:text-5xl font-semibold text-foreground">₹299</span>
                <div>
                  <p className="text-muted line-through text-base lg:text-lg">₹499</p>
                  <p className="text-primary text-xs lg:text-sm font-semibold">{t('finalCta.saveText')}</p>
                </div>
              </div> */}

              <button
                onClick={handleBuyNow}
                // FIX 3: Scaled button padding for mobile 
                className="btn-primary w-full sm:w-auto px-8 py-4 lg:px-14 lg:py-5 rounded-2xl font-semibold text-base lg:text-lg tracking-wide shadow-green-lg animate-pulse-glow active:scale-[0.98] transition-transform"
                aria-label="Buy Dr. Detoxer — protect your family from pesticides">
                {t('finalCta.buyButton')}
              </button>

              {/* Payment icons */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 lg:gap-4">
                <span className="text-muted text-[10px] lg:text-xs">{t('finalCta.paymentLabel')}</span>
                {['UPI', 'Visa', 'Mastercard', 'RuPay', 'Net Banking']?.map((p) =>
                  <span
                    key={p}
                    className="bg-white border border-border px-2.5 py-1 lg:px-3 lg:py-1.5 rounded-xl text-[10px] lg:text-xs font-semibold text-foreground shadow-soft">
                    {p}
                  </span>
                )}
              </div>

              {/* Trust row */}
              <div className="flex flex-wrap justify-center lg:justify-start gap-4 lg:gap-6 text-xs lg:text-sm text-muted pt-2">
                <span className="flex items-center gap-1.5"><span>🔒</span> {t('finalCta.trust.ssl')}</span>
                <span className="flex items-center gap-1.5"><span>🚚</span> {t('finalCta.trust.delivery')}</span>
                {/* <span className="flex items-center gap-1.5"><span>↩️</span> {t('finalCta.trust.returns')}</span> */}
                <span className="flex items-center gap-1.5"><span>📞</span> {t('finalCta.trust.support')}</span>
              </div>
            </motion.div>
          </motion.div>

          {/* Right: Product image */}
          <motion.div
            className="col-span-1 lg:col-span-5 lg:col-start-8 order-1 lg:order-2 flex justify-center w-full"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}>
            
            {/* FIX 4: Constrained image wrapper width for mobile (`max-w-[280px] sm:max-w-sm lg:max-w-none`) */}
            <div className="relative w-full max-w-[280px] sm:max-w-sm lg:max-w-none mx-auto">
              {/* Glow */}
              <div
                className="absolute inset-0 rounded-full blur-3xl opacity-30 pointer-events-none"
                style={{
                  background: 'radial-gradient(circle, #2d6b4f 0%, #2a7fbf 50%, transparent 80%)'
                }}
                aria-hidden="true" />
              
              <motion.div
                className="relative animate-float-slow w-full"
                whileHover={{ scale: 1.03 }}
                transition={{ duration: 0.4 }}>
                
                <AppImage
                  src="/assets/images/hero_section.png"
                  alt="Dr. Detoxer vegetable and fruit cleaning liquid — buy now and protect your family"
                  width={500}
                  height={580}
                  // FIX 5: Adjusted border radius for mobile
                  className="w-full h-auto object-cover rounded-4xl lg:rounded-5xl shadow-green-lg" />
                
              </motion.div>
            </div>
          </motion.div>
          
        </div>
      </div>
    </section>
  );
}